import React, { useMemo, useState } from 'react';
import { Button, HStack, Box, Text, ButtonGroup } from '@chakra-ui/react';
import { FiPlus, FiChevronLeft, FiChevronRight, FiGlobe, FiSmartphone, FiActivity } from 'react-icons/fi';
import { LuLink2 } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import PageHeading from '../../../../components/shared/PageHeading';
import { useListLinksQuery } from '../store/linksApi';
import { LinkFilters, type FilterValue } from '../components/links/LinkFilters';
import { LinksTable } from '../components/links/LinksTable';
import { CreateLinkModal } from '../components/links/CreateLinkModal';
import { AppProfileModal } from '../components/links/AppProfileModal';
import { ExpireLinkDialog } from '../components/links/ExpireLinkDialog';
import { QrCodeModal } from '../components/links/QrCodeModal';
import { LoadingState, ErrorState, EmptyState } from '../components/common/States';
import type { Link } from '../types/link';
import { formatNumber } from '../lib/format';
import { useDebouncedValue } from '../lib/useDebouncedValue';

const PAGE_SIZE = 10;

// Permission gating (added by host app — see SmartLinks.jsx): both default to
// `true` so this still behaves exactly as before when rendered without props.
interface LinksListPageProps {
  createPermission?: boolean;
  editPermission?: boolean;
}

export const LinksListPage: React.FC<LinksListPageProps> = ({ createPermission = true, editPermission = true }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterValue>({ search: '', status: 'all' });
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [appProfileOpen, setAppProfileOpen] = useState(false);
  const [expiring, setExpiring] = useState<Link | null>(null);
  const [qrLink, setQrLink] = useState<Link | null>(null);

  // SM_009: debounce the search term so typing fires ONE request after a pause,
  // not one per keystroke (RTK Query also de-dupes/aborts when the arg changes).
  const debouncedSearch = useDebouncedValue(filters.search, 300);

  const params = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      status: filters.status,
      page,
      pageSize: PAGE_SIZE,
      sortBy: 'createdAt' as const,
      sortDir: 'desc' as const,
    }),
    [debouncedSearch, filters.status, page],
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useListLinksQuery(params);

  const onFilterChange = (next: FilterValue) => {
    setFilters(next);
    setPage(1);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const noLinksAtAll =
    !isLoading &&
    data?.total === 0 &&
    !filters.search &&
    filters.status === 'all';

  return (
    <Box>
      <PageHeading
        icon={LuLink2}
        title="Smart Links"
        subtitle={data
          ? `${formatNumber(data.total)} link${data.total === 1 ? '' : 's'}`
          : 'Manage tenant short links'}
      >
        <Button leftIcon={<FiActivity />} variant="outline" onClick={() => navigate('activity')}>
          Activity
        </Button>
        <Button leftIcon={<FiGlobe />} variant="outline" onClick={() => navigate('settings/domain')}>
          Custom domain
        </Button>
        <Button leftIcon={<FiSmartphone />} variant="outline"  onClick={() => setAppProfileOpen(true)}>
          App Profile
        </Button>
        <Button
          bg={"var(--brand-primary)"}
          leftIcon={<FiPlus />}
          onClick={() => setCreateOpen(true)}
          isDisabled={!createPermission}
        >
          Create link
        </Button>
      </PageHeading>

      <Box mb={4}>
        <LinkFilters value={filters} onChange={onFilterChange} />
      </Box>

      {isLoading ? (
        <LoadingState label="Loading links…" />
      ) : isError ? (
        <ErrorState
          message={(error as { message?: string })?.message ?? 'Failed to load links'}
          onRetry={refetch}
        />
      ) : noLinksAtAll ? (
        <EmptyState
          title="No links yet"
          hint="Create your first short link to start tracking clicks and app installs."
          action={
            <Button leftIcon={<FiPlus />} onClick={() => setCreateOpen(true)} isDisabled={!createPermission}>
              Create link
            </Button>
          }
        />
      ) : (
        <>
          <Box opacity={isFetching ? 0.6 : 1} transition="opacity 0.15s">
            <LinksTable
              links={data?.items ?? []}
              onExpire={setExpiring}
              onShowQr={setQrLink}
              editPermission={editPermission}
            />
          </Box>
          <HStack mt={4} justify="space-between">
            <Text fontSize="sm" color="gray.500">
              Page {page} of {totalPages}
            </Text>
            <ButtonGroup size="sm" variant="outline" isAttached>
              <Button leftIcon={<FiChevronLeft />} isDisabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Prev
              </Button>
              <Button
                rightIcon={<FiChevronRight />}
                isDisabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </ButtonGroup>
          </HStack>
        </>
      )}

      <CreateLinkModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />
      {/* The "app-profile save" permission gate actually lives here, not on
          LinkDetailPage — AppProfileModal is only ever rendered from this page. */}
      <AppProfileModal
        isOpen={appProfileOpen}
        onClose={() => setAppProfileOpen(false)}
        editPermission={editPermission}
      />
      <ExpireLinkDialog link={expiring} onClose={() => setExpiring(null)} />
      <QrCodeModal
        isOpen={!!qrLink}
        onClose={() => setQrLink(null)}
        url={qrLink?.shortUrl ?? ''}
        filenameBase={qrLink ? `qr-${qrLink.shortCode}` : 'qr'}
      />
    </Box>
  );
};
